/** @format */

import { cssVars } from '@atoms/GlobalStyles';
import useNotificationActions from '@common/application/hooks/useNotificationActions/useNotificationActions';
import useStrings from '@common/application/hooks/useStrings/useStrings';
import { Notification } from '@common/model/apiClient/Notification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useParams } from 'next/navigation';
import React from 'react';
import styled from 'styled-components';
import styles from './NotificationCard.module.css';

interface NotificationProps {
  notification: Notification;
}

const StyledNotification = styled.div<{ isRead: boolean }>`
  height: 50px;
  display: flex;
  //cursor: pointer;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  padding: 4px;
  //padding-inline: 4px;
  font-size: 0.9rem;
  position: relative;
  background: ${(props) => (!props.isRead ? 'var(--primary-500)' + '15' : 'transparent')};

  &:hover {
    background: var(--primary-100);
  }
  &:before {
    content: '';
    position: absolute;
    left: 4%;
    bottom: 0;
    height: 1px;
    width: 92%;
    border-bottom: 1px solid var(--grey-100);
  }
  :not(:last-of-type) {
  }
`;

const NotificationBox = styled.div<{ isRead: boolean; hasClickEvent: boolean }>`
  padding: 16px;
  cursor: ${(props) => (props.hasClickEvent ? 'pointer' : 'default')};
  > div:first-of-type {
    color: ${(props) => (!props.isRead ? 'var(--black)' : 'var(--grey-200)')};
    //font-weight: ${(props) => (props.isRead ? 'regular' : 'bold')};
    // font-size: ${(props) => (!props.isRead ? '1rem' : '0.9rem')};
  }
  > div:last-of-type {
    color: var(--grey-200);
    font-size: 0.8rem;
  }
`;

function NotificationCard({ notification }: NotificationProps) {
  const { notificationStrings } = useStrings();
  const params = useParams<{ locale: string }>();
  const locale = params?.locale;
  const clickEvent = useNotificationActions(notification);

  const queryClient = useQueryClient();

  const deleteNotification = useMutation({
    mutationKey: ['delete_notification'],
    mutationFn: (notificationId: number) => Notification.delete(notificationId),
    onSuccess: () => queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes('user_notification_query') }),
  });

  const markRead = useMutation({
    mutationKey: ['mark_all_notifications_read'],
    mutationFn: (notificationId: number) => Notification.markRead(notificationId),
    onSuccess: () => queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes('user_notification_query') }),
  });

  return (
    <div className={clsx(styles.notification, { [styles.read]: notification.read })}>
      <div
        className={clsx(styles.content, { [styles.read]: notification.read })}
        style={{
          cursor: !!clickEvent ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (clickEvent) {
            clickEvent();
          }
          markRead.mutate(notification.id);
        }}
      >
        <div>{notificationStrings[notification.table]?.[notification.event]}</div>
        <div>{new Date(notification.createdAt).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })}</div>
      </div>
      <span className={styles.delete} onClick={() => deleteNotification.mutate(notification.id)}>
        <FontAwesomeIcon icon={['far', 'trash-xmark']} />
      </span>
    </div>
  );
}

export default NotificationCard;
